import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useDutyStore } from '@/rennovation/planning-view/store/useDutyStore';
import type { ApiDutiesResponse, ApiDutySummary, DutyAvailabilityResponse } from '@/rennovation/planning-view/types/api/duties-response';
import { AssignDutyApiPayload, AssignDutyApiResponse } from '@/rennovation/planning-view/types/api/assign-duty-api-response';
import { useDutyApi } from '@/rennovation/common/services/api/useDutyApi';
import { CreateDutyPayload } from '@/rennovation/planning-view/types/payloads/duty-payloads';
import { useAlertStore } from '@/rennovation/planning-view/store/useAlertStore';
import { useI18n } from 'vue-i18n';

type DispositionGroups = DutyAvailabilityResponse['data'];

// ============================================
// TYPES
// ============================================
export type AssignDutyEntryPoint = 'driver_row' | 'unallocated_block';

export interface AssignDutyTarget {
	date: string;
	entryPoint: AssignDutyEntryPoint;
	driverId: string | null;
	driverName: string | null;
}

const mapSummaries = (items: ApiDutySummary[]): (ApiDutySummary & { id: string | number })[] =>
	items.map((item: ApiDutySummary) => ({
		...item,
		id: item._id,
	}));

// ============================================
// STORE
// ============================================
export const usePlanningAssignDutyStore = defineStore('planningAssignDuty', () => {
	const { t } = useI18n();

	const dutyApi = useDutyApi();
	const alertStore = useAlertStore();
	const createEmptyDispositions = (): DispositionGroups => ({
		unallocated: [],
		reserve: [],
		all: [],
	});
	// ----------------------------------------
	// STATE
	// ----------------------------------------
	const isModalOpen = ref(false);
	const targetDate = ref<string | null>(null);
	const assignTarget = ref<AssignDutyTarget | null>(null);
	const currentDayId = ref<string | null>(null);
	const isDriverLocked = ref(false);
	const preselectedDriverId = ref<string | null>(null);
	const preselectedDriverName = ref<string>('');

	const availableDispositions = ref<DispositionGroups>(createEmptyDispositions());
	const dispositionsLoading = ref(false);
	const dispositionsFetchError = ref<string | null>(null);

	// ----------------------------------------
	// ACTIONS
	// ----------------------------------------

	const _fetchDispositionsForDate = async (date: string): Promise<void> => {
		const dutyStore = useDutyStore();
		dispositionsLoading.value = true;
		dispositionsFetchError.value = null;

		try {
			const response = await dutyStore.fetchAvailableDutiesByDay(date);
			currentDayId.value = response?.day ?? null;

			availableDispositions.value = {
				unallocated: mapSummaries(response?.data?.unallocated ?? []),
				reserve: mapSummaries(response?.data?.reserve ?? []),
				all: mapSummaries(response?.data?.all ?? []),
			};
		} catch (error) {
			dispositionsFetchError.value = error instanceof Error ? error.message : 'Unknown error';
			availableDispositions.value = { unallocated: [], reserve: [], all: [] };
			console.error('[usePlanningAssignDutyStore] Failed to fetch dispositions:', error);

			const fallbackMsg = t('duties_load_failed');
			const backendMsg = error?.response?.data?.message || error?.message;
			alertStore.showAlert(backendMsg || fallbackMsg, 'error');
		} finally {
			dispositionsLoading.value = false;
		}
	};

	const assignDutyToDriver = async (dutyId: string, payload: AssignDutyApiPayload): Promise<AssignDutyApiResponse> => {
		try {
			const apiPayload: AssignDutyApiPayload = {
				user: payload.user,
				fixed: payload.fixed,
				fixedBy: payload.fixedBy,
				day: payload.day,
			};

			return await dutyApi.updateDuty(dutyId, apiPayload);
		} catch (error) {
			console.error('[usePlanningAssignDutyStore] assignDutyToDriver failed:', error);
			throw error;
		}
	};

	const createStatusDuty = async (payload: CreateDutyPayload): Promise<ApiDutiesResponse> => {
		try {
			return await dutyApi.createDuty(payload);
		} catch (error) {
			console.error('[usePlanningAssignDutyStore] createStatusDuty failed:', error);
			throw error;
		}
	};

	async function openModal(params: {
		date: string;
		dayId?: string | null;
		driverId?: string | null;
		driverName?: string;
		isLocked?: boolean;
	}): Promise<void> {
		targetDate.value = params.date;
		currentDayId.value = params.dayId;
		preselectedDriverId.value = params.driverId ?? null;
		preselectedDriverName.value = params.driverName ?? '';
		isDriverLocked.value = params.isLocked ?? false;

		await _fetchDispositionsForDate(params.date);

		isModalOpen.value = true;
	}

	function closeModal(): void {
		isModalOpen.value = false;
		targetDate.value = null;
		currentDayId.value = null;
		preselectedDriverId.value = null;
		preselectedDriverName.value = '';
		isDriverLocked.value = false;
		availableDispositions.value = createEmptyDispositions();
	}

	return {
		// state
		isModalOpen,
		assignTarget,
		availableDispositions,
		dispositionsLoading,
		dispositionsFetchError,
		currentDayId,

		// computed
		isDriverLocked,
		preselectedDriverId,
		preselectedDriverName,
		targetDate,

		// actions
		openModal,
		closeModal,
		assignDutyToDriver,
		createStatusDuty,
	};
});
